<template>
  <div class="d-flex flex-column">
    <DxDataGrid
      v-bind="{ ...getDefaultGridProperties(), ...getVirtualScrollingGridProperties() }"
      :data-source="gridSource"
      ref="grid"
      class="dx-card wide-card grid-liquidaciones"
      @selectionChanged="handleSelectionChanged"
    >
      <DxToolbar :visible="!props.filtroPendientes">
        <DxItem location="before" template="filtrarPendientesTemplate" />
      </DxToolbar>
      <DxSelection
        v-if="props.filasSeleccionables"
        select-all-mode="allPages"
        show-check-boxes-mode="always"
        :selectedRowKeys="liquidacionesSeleccionadas"
        mode="multiple"
      />

      <DxColumn :group-index="0" data-field="NombreTramite" group-cell-template="tramiteGroupTemplate" />
      <DxColumn data-field="CodigoSecuencial" :caption="$t('liquidaciones.grid.id')" format="0000000000"></DxColumn>
      <DxColumn data-field="Liquidado" :visible="!props.reduced" :caption="$t('liquidaciones.grid.liquidado')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="CodigoEstado" :visible="!props.reduced" :caption="$t('liquidaciones.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TipoConcepto" :caption="$t('liquidaciones.grid.tipo_concepto')">
        <DxLookup column-auto-width="true" v-bind="getLookup('liquidaciones', 'tipo_concepto')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Agente" :caption="$t('liquidaciones.grid.agente')"></DxColumn>
      <DxColumn
        data-field="ComisionEfectiva"
        :caption="$t('liquidaciones.grid.comision')"
        data-type="number"
        :calculate-cell-value="formatComisionEfectiva"
      >
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn
        data-field="RetrocomisionEfectiva"
        :caption="$t('liquidaciones.grid.retrocomision')"
        data-type="number"
        :calculate-cell-value="formatRetrocomisionEfectiva"
        :calculate-filter-expression="retrocomisionEfectivaFilter"
      >
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="FechaActivacion" :caption="$t('liquidaciones.grid.fecha_activacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaBaja" :visible="!props.reduced" :caption="$t('liquidaciones.grid.fecha_baja')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="DiasAlta" :visible="!props.reduced" :caption="$t('liquidaciones.grid.dias_alta')" data-type="number"></DxColumn>

      <DxColumn data-field="Comercializadora" :visible="!props.reduced" :caption="$t('liquidaciones.grid.comercializadora')"></DxColumn>
      <DxColumn data-field="TipoSuministro" :visible="!props.reduced" :caption="$t('liquidaciones.grid.grid.tipo_suministro')">
        <DxLookup column-auto-width="true" v-bind="getLookup('rappels_mensuales', 'tipo_suministro')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="DocumentoCliente" :visible="!props.reduced" :caption="$t('liquidaciones.grid.documento_cliente')"></DxColumn>
      <DxColumn data-field="DireccionCliente" :visible="!props.reduced" :caption="$t('liquidaciones.grid.direccion_cliente')"></DxColumn>
      <DxColumn
        type="buttons"
        :caption="$t('liquidaciones.grid.acciones')"
        :visible="!props.reduced"
        :fixed="true"
        fixed-position="right"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-solid fa-magnifying-glass text-success',
            hint: $t('liquidaciones.grid.ver_detalle'),
            onClick: (e: any) => verDetalle(e),
          },
        ]"
      />

      <DxSummary>
        <DxGroupItem
          :show-in-group-footer="false"
          :align-by-column="true"
          column="ComisionEfectiva"
          summary-type="sum"
          display-format="Total: {0}"
          :value-format="formatComisionSum"
        >
        </DxGroupItem>
        <DxGroupItem
          :show-in-group-footer="false"
          :align-by-column="true"
          column="RetrocomisionEfectiva"
          summary-type="sum"
          display-format="Total: {0}"
          :value-format="formatRetrocomisionSum"
        >
        </DxGroupItem>
      </DxSummary>

      <DxColumnFixing :enabled="true" />

      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #estadoTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2`, `estado-operacion ${data.value.replace(' ', '')}`]">
            {{ $t(`operaciones.estado.${data.value}`) }}
        </div>
      </template>
      <template #filtrarPendientesTemplate>
        <DxButton
          v-bind="secondaryButtonProps"
          :text="$t('liquidaciones.grid.btn_liquidaciones')"
          @click="toggleLiquidacionesPendientes"
          :class="[
            'ms-3 btn-filtrar-pendientes dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-template-wrapper dx-item-content dx-toolbar-item-content',
            { active: filtroPendientes },
          ]"
        />
      </template>
      <template #tramiteGroupTemplate="{ data }">
        <span>
          {{ data.value }}
        </span>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import { DxColumn, DxDataGrid, DxFormat, DxLookup, DxColumnFixing, DxToolbar, DxItem, DxSelection, DxSummary, DxGroupItem } from "devextreme-vue/data-grid";
import {
  currencyFormat,
  getDefaultGridProperties,
  getLookup,
  getColumnLookupBoolean,
  getRetrocomisionFilter,
  getVirtualScrollingGridProperties,
} from "@helpers/grid-helper";
import {
  comisionEfectivaLiquidacion,
  esLiquidacionRetrocomision,
  importeRetribucionParaMostrar,
  retrocomisionEfectivaLiquidacion,
} from "@helpers/retribucion-economica-helper";
import { useModalService } from "@services/modal-service";
import type CustomStore from "devextreme/data/custom_store";
import { onMounted, ref, h } from "vue";
import OperacionesResumen from "@/components/operaciones-energia/OperacionesEnergiaResumen.vue";
import type DevExpress from "devextreme";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import { OperacionEnergia } from "@/components/nueva-venta/models/OperacionEnergia";
import { DxButton } from "devextreme-vue";
import { secondaryButtonProps } from "@/.shared/helpers/button-helper";
import { type DXLoadOptions, type LoadResult } from "@/.shared/.api";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";

const props = defineProps({
  filtroPendientes: { type: Boolean, default: false },
  reduced: { type: Boolean, default: false },
  filasSeleccionables: { type: Boolean, default: false },
  seleccionarTodas: { type: Boolean, default: false },
});

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const modal = useModalService();
const intl = useIntl();
const filtroPendientes = ref<boolean>(props.filtroPendientes);
const liquidacionesSeleccionadas = ref<Array<object>>([]);
const emit = defineEmits(["actualizarLiquidaciones"]);
let inicializado = false;

onMounted(async () => {
  gridSource.value = await api.apiStore({
    key: ["IdTramite", "IdServicioIncluido"],
    load: async (p) => {
      // Preselecciones en la carga inicial
      if (!inicializado && props.filasSeleccionables && props.seleccionarTodas) {
        grid.value?.instance?.selectAll();
      }

      const q = p as DXLoadOptions;
      if (filtroPendientes.value) {
        if (q.filter != null && q.filter.length >= 0) q.filter = [["Liquidado", "=", "false"], "and", q.filter];
        else q.filter = ["Liquidado", "=", "false"];
      }
      const result = await api.callStandardApi("getLiquidacionesForGrid", { showLoading: false }, q);

      // Preselecciones de las retrocomisiones (se hace seleccionando todas y luego deseleccionando las normales)
      if (!inicializado && props.filasSeleccionables && !props.seleccionarTodas) {
        grid.value?.instance?.selectAll();
        selectRetrocomisiones(result);
      }

      inicializado = true;
      return result;
    },
  });
});

function retrocomisionEfectivaFilter(filterValue: unknown, selectedFilterOperation: string) {
  return getRetrocomisionFilter(filterValue, selectedFilterOperation, "RetrocomisionEfectiva");
}

async function selectRetrocomisiones(result: LoadResult) {
  const predicate = (row: any) => !esLiquidacionRetrocomision(row);
  const rowsToSelect = result.data?.filter(predicate);
  const rowKeysToSelect = rowsToSelect?.map((row: any) => [row.IdTramite, row.IdServicioIncluido]);

  if (rowKeysToSelect) grid.value?.instance?.deselectRows(rowKeysToSelect);
}

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let title = `${intl.i18n.t("operaciones.detalle.titulo")} ${data.row?.data.CodigoSecuencial.toString().padStart(10, "0")}`;

  var response = await api.callStandardApi("getOperacionEnergiaDetail", { showLoading: true }, data.row?.data.IdOperacion);
  var model = new OperacionEnergia();
  model.createFromOperacionDetail(response);

  await modal.showModal(OperacionesResumen, { data: model }, { title: title });
}

const toggleLiquidacionesPendientes = () => {
  filtroPendientes.value = !filtroPendientes.value;

  if (filtroPendientes.value) {
    grid.value?.instance?.filter(["Liquidado", "=", false]);
  } else {
    grid.value?.instance?.filter([["Liquidado", "=", true], "or", ["Liquidado", "=", false]]);
  }
};

const formatComisionEfectiva = (rowData: any) => comisionEfectivaLiquidacion(rowData);

const formatRetrocomisionEfectiva = (rowData: any) => {
  const retro = retrocomisionEfectivaLiquidacion(rowData);
  return importeRetribucionParaMostrar(retro, true);
};

const formatComisionSum = (value: number | null) => {
  return value ? `${value.toFixed(2).replace(".", ",")} €` : "0";
};

const formatRetrocomisionSum = (value: number | null) => {
  if (!value) return "0";
  const mostrado = importeRetribucionParaMostrar(value, true) ?? 0;
  return `${mostrado.toFixed(2).replace(".", ",")} €`;
};

const handleSelectionChanged = (e: any) => {
  const selectedRows = e.selectedRowsData;
  liquidacionesSeleccionadas.value = selectedRows.map((row: any) => row);
  emit("actualizarLiquidaciones", liquidacionesSeleccionadas.value);
};

const refreshGrid = () => {
  grid.value?.instance?.refresh();
};

defineExpose({ refreshGrid });
</script>

<style lang="scss" scoped>


.lupa {
  color: var(--text-secondary-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}

.btn-filtrar-pendientes {
  &.active {
    background-color: var(--button-primary-default);
    color: var(--text-primary-on-action);
  }
}

.grid-liquidaciones {
  height: 450px;
}
</style>
